import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ArrowRight, Building2, Euro, Calculator as CalcIcon, FileText, Check, MapPin, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { DROM_DEPARTMENTS, DROM_COMMUNES, PINEL_OUTRE_MER_RATES } from '../data/drom-data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'villa', label: 'Villa' },
  { value: 'studio', label: 'Studio' },
  { value: 'local_commercial', label: 'Local commercial' }
];

export default function Calculator() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [step, setStep] = useState(1);

  useEffect(() => {
    const checkQuota = async () => {
      try {
        const response = await axios.get(`${API}/projects/quota`, { headers: getAuthHeader() });
        if (!response.data.can_create) {
          if (response.data.free_project_used) {
            toast.error('Votre projet gratuit a été utilisé. Choisissez un abonnement pour continuer.');
          } else {
            toast.error(`Crédits mensuels utilisés (${response.data.current}/${response.data.max}). Passez à un plan supérieur !`);
          }
          navigate('/pricing');
        }
      } catch (error) {
        console.error('Error checking quota:', error);
      }
    };
    checkQuota();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    property_type: '',
    department: '',
    location: '',
    purchase_price: '',
    notary_fees: '',
    renovation_cost: '',
    monthly_rent: '',
    down_payment: '',
    loan_amount: '',
    interest_rate: '',
    loan_duration_years: '',
    property_tax: '',
    condo_fees: '',
    insurance: '',
    management_fees: '',
    maintenance: '',
    pinel_eligible: false,
    pinel_duration: '',
    girardin_eligible: false,
    girardin_reduction: ''
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Reset commune when department changes
      if (field === 'department') {
        next.location = '';
      }
      return next;
    });
  };

  const communes = formData.department ? DROM_COMMUNES[formData.department] || [] : [];
  const isFiscalAutonomy = formData.location.startsWith('st_barth') || formData.location.startsWith('st_martin');

  const isStepValid = (s) => {
    switch (s) {
      case 1:
        return formData.name.trim() !== '' &&
          formData.property_type !== '' &&
          formData.department !== '' &&
          formData.location !== '' &&
          formData.purchase_price !== '' &&
          formData.notary_fees !== '' &&
          formData.monthly_rent !== '';
      case 2:
        return formData.down_payment !== '' &&
          formData.loan_amount !== '' &&
          formData.interest_rate !== '' &&
          formData.loan_duration_years !== '';
      case 3:
        return formData.property_tax !== '' &&
          formData.insurance !== '';
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (step < totalSteps && isStepValid(step)) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCalculate = async () => {
    try {
      const quotaRes = await axios.get(`${API}/projects/quota`, { headers: getAuthHeader() });
      if (!quotaRes.data.can_create) {
        if (quotaRes.data.free_project_used) {
          toast.error('Votre projet gratuit a été utilisé. Choisissez un abonnement pour continuer.');
        } else {
          toast.error(`Crédits mensuels utilisés (${quotaRes.data.current}/${quotaRes.data.max}). Passez à un plan supérieur !`);
        }
        navigate('/pricing');
        return;
      }

      const projectData = {
        name: formData.name,
        property_type: formData.property_type,
        department: formData.department,
        location: formData.location,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        notary_fees: parseFloat(formData.notary_fees) || 0,
        renovation_cost: parseFloat(formData.renovation_cost) || 0,
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        financing: {
          total_cost: parseFloat(formData.purchase_price) + parseFloat(formData.notary_fees) + parseFloat(formData.renovation_cost) || 0,
          down_payment: parseFloat(formData.down_payment) || 0,
          loan_amount: parseFloat(formData.loan_amount) || 0,
          interest_rate: parseFloat(formData.interest_rate) || 0,
          loan_duration_years: parseInt(formData.loan_duration_years) || 0
        },
        charges: {
          property_tax: parseFloat(formData.property_tax) || 0,
          condo_fees: parseFloat(formData.condo_fees) || 0,
          insurance: parseFloat(formData.insurance) || 0,
          management_fees: parseFloat(formData.management_fees) || 0,
          maintenance: parseFloat(formData.maintenance) || 0
        },
        tax_benefits: {
          pinel_eligible: formData.pinel_eligible,
          pinel_duration: formData.pinel_eligible ? parseInt(formData.pinel_duration) : null,
          girardin_eligible: formData.girardin_eligible,
          girardin_reduction: formData.girardin_eligible ? parseFloat(formData.girardin_reduction) : null
        }
      };

      const response = await axios.post(`${API}/projects`, projectData, {
        headers: getAuthHeader()
      });
      toast.success('Projet créé avec succès !');
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Quota de projets atteint');
        navigate('/pricing');
      } else {
        toast.error('Erreur lors de la creation du projet');
      }
    }
  };

  return (
    <div className="min-h-screen bg-warm-sand py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Button
            data-testid="back-to-home-btn"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 text-[#005F73] hover:text-[#005F73]/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Retour à l'accueil
          </Button>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#005F73] mb-4 tracking-tight">
            Calculateur de rentabilité
          </h1>
          <p className="text-lg text-stone-600 leading-relaxed">
            Remplissez les informations de votre projet immobilier Outre-mer
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" data-testid="calculator-progress" />
          <div className="flex justify-between mt-3 text-sm text-stone-600">
            <span className={step >= 1 ? 'text-[#005F73] font-medium' : ''}>Bien immobilier</span>
            <span className={step >= 2 ? 'text-[#005F73] font-medium' : ''}>Financement</span>
            <span className={step >= 3 ? 'text-[#005F73] font-medium' : ''}>Charges</span>
            <span className={step >= 4 ? 'text-[#005F73] font-medium' : ''}>Fiscalité</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-stone-100 p-8 md:p-10 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]">
          {/* Step 1: Property Info */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-property-info">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#005F73]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#005F73]" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-semibold text-[#005F73]">Informations du bien</h2>
              </div>

              <div>
                <Label htmlFor="name">Nom du projet *</Label>
                <Input
                  data-testid="input-project-name"
                  id="name"
                  placeholder="ex: Villa Les Abymes"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="property_type">Type de bien *</Label>
                <Select onValueChange={(value) => handleInputChange('property_type', value)} value={formData.property_type}>
                  <SelectTrigger data-testid="select-property-type" className="h-12 rounded-xl">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* DROM Department selector */}
              <div>
                <Label htmlFor="department" className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-[#005F73]" />
                  Département DROM *
                </Label>
                <Select onValueChange={(value) => handleInputChange('department', value)} value={formData.department}>
                  <SelectTrigger data-testid="select-department" className="h-12 rounded-xl">
                    <SelectValue placeholder="Sélectionnez un département" />
                  </SelectTrigger>
                  <SelectContent>
                    {DROM_DEPARTMENTS.map(dept => (
                      <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Commune selector (depends on department) */}
              <div>
                <Label htmlFor="location">Commune *</Label>
                <Select
                  onValueChange={(value) => handleInputChange('location', value)}
                  value={formData.location}
                  disabled={!formData.department}
                >
                  <SelectTrigger data-testid="select-location" className="h-12 rounded-xl">
                    <SelectValue placeholder={formData.department ? "Sélectionnez une commune" : "Choisissez d'abord un département"} />
                  </SelectTrigger>
                  <SelectContent>
                    {communes.map(loc => (
                      <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="purchase_price">Prix d'achat (EUR) *</Label>
                <Input
                  data-testid="input-purchase-price"
                  id="purchase_price"
                  type="number"
                  placeholder="200000"
                  value={formData.purchase_price}
                  onChange={(e) => handleInputChange('purchase_price', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="notary_fees">Frais de notaire (EUR) *</Label>
                <Input
                  data-testid="input-notary-fees"
                  id="notary_fees"
                  type="number"
                  placeholder="15000"
                  value={formData.notary_fees}
                  onChange={(e) => handleInputChange('notary_fees', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="renovation_cost">Coût des travaux (EUR)</Label>
                <Input
                  data-testid="input-renovation-cost"
                  id="renovation_cost"
                  type="number"
                  placeholder="10000"
                  value={formData.renovation_cost}
                  onChange={(e) => handleInputChange('renovation_cost', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="monthly_rent">Loyer mensuel estimé (EUR) *</Label>
                <Input
                  data-testid="input-monthly-rent"
                  id="monthly_rent"
                  type="number"
                  placeholder="1200"
                  value={formData.monthly_rent}
                  onChange={(e) => handleInputChange('monthly_rent', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* Step 2: Financing */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-financing">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#EE9B00]/10 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-[#EE9B00]" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-semibold text-[#005F73]">Financement</h2>
              </div>

              <div>
                <Label htmlFor="down_payment">Apport personnel (EUR) *</Label>
                <Input
                  data-testid="input-down-payment"
                  id="down_payment"
                  type="number"
                  placeholder="40000"
                  value={formData.down_payment}
                  onChange={(e) => handleInputChange('down_payment', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="loan_amount">Montant du prêt (EUR) *</Label>
                <Input
                  data-testid="input-loan-amount"
                  id="loan_amount"
                  type="number"
                  placeholder="185000"
                  value={formData.loan_amount}
                  onChange={(e) => handleInputChange('loan_amount', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="interest_rate">Taux d'intérêt (%) *</Label>
                <Input
                  data-testid="input-interest-rate"
                  id="interest_rate"
                  type="number"
                  step="0.01"
                  placeholder="3.5"
                  value={formData.interest_rate}
                  onChange={(e) => handleInputChange('interest_rate', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="loan_duration_years">Durée du prêt (années) *</Label>
                <Input
                  data-testid="input-loan-duration"
                  id="loan_duration_years"
                  type="number"
                  placeholder="20"
                  value={formData.loan_duration_years}
                  onChange={(e) => handleInputChange('loan_duration_years', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* Step 3: Charges */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-charges">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#0A9396]/10 flex items-center justify-center">
                  <CalcIcon className="w-5 h-5 text-[#0A9396]" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-semibold text-[#005F73]">Charges annuelles et mensuelles</h2>
              </div>

              <div>
                <Label htmlFor="property_tax">Taxe foncière (EUR/an) *</Label>
                <Input
                  data-testid="input-property-tax"
                  id="property_tax"
                  type="number"
                  placeholder="800"
                  value={formData.property_tax}
                  onChange={(e) => handleInputChange('property_tax', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="condo_fees">Charges de copropriété (EUR/mois)</Label>
                <Input
                  data-testid="input-condo-fees"
                  id="condo_fees"
                  type="number"
                  placeholder="80"
                  value={formData.condo_fees}
                  onChange={(e) => handleInputChange('condo_fees', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="insurance">Assurance PNO (EUR/mois) *</Label>
                <Input
                  data-testid="input-insurance"
                  id="insurance"
                  type="number"
                  placeholder="30"
                  value={formData.insurance}
                  onChange={(e) => handleInputChange('insurance', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="management_fees">Frais de gestion locative (EUR/mois)</Label>
                <Input
                  data-testid="input-management-fees"
                  id="management_fees"
                  type="number"
                  placeholder="100"
                  value={formData.management_fees}
                  onChange={(e) => handleInputChange('management_fees', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="maintenance">Entretien et réparations (EUR/mois)</Label>
                <Input
                  data-testid="input-maintenance"
                  id="maintenance"
                  type="number"
                  placeholder="50"
                  value={formData.maintenance}
                  onChange={(e) => handleInputChange('maintenance', e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* Step 4: Tax Benefits */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-tax-benefits">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#94D2BD]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#0A9396]" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-semibold text-[#005F73]">Dispositifs fiscaux Outre-mer</h2>
              </div>

              {isFiscalAutonomy && (
                <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 flex items-start gap-3" data-testid="fiscal-autonomy-warning">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Collectivite a autonomie fiscale</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Saint-Barthélemy et Saint-Martin disposent de leur propre régime fiscal depuis 2007. Les dispositifs Pinel Outre-mer et Girardin ne s'appliquent pas sur ces territoires. La simulation sera effectuée sans avantage fiscal.
                    </p>
                  </div>
                </div>
              )}

              <div className={`bg-stone-50 rounded-xl p-6 border border-stone-200 ${isFiscalAutonomy ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label htmlFor="pinel" className="text-base font-medium">Éligible Pinel Outre-Mer</Label>
                    <p className="text-sm text-stone-600 mt-1">Réduction d'impôt majorée pour l'Outre-mer</p>
                  </div>
                  <Switch
                    data-testid="switch-pinel"
                    id="pinel"
                    checked={!isFiscalAutonomy && formData.pinel_eligible}
                    onCheckedChange={(checked) => handleInputChange('pinel_eligible', checked)}
                    disabled={isFiscalAutonomy}
                  />
                </div>

                {formData.pinel_eligible && !isFiscalAutonomy && (
                  <div className="mt-4">
                    <Label htmlFor="pinel_duration">Durée d'engagement (années)</Label>
                    <Select onValueChange={(value) => handleInputChange('pinel_duration', value)} value={formData.pinel_duration}>
                      <SelectTrigger data-testid="select-pinel-duration" className="h-12 rounded-xl">
                        <SelectValue placeholder="Choisir la durée" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PINEL_OUTRE_MER_RATES).map(([years, { label }]) => (
                          <SelectItem key={years} value={years}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#0A9396] mt-2">Taux majorés Outre-mer : jusqu'à 32% de réduction sur 12 ans</p>
                  </div>
                )}
              </div>

              <div className={`bg-stone-50 rounded-xl p-6 border border-stone-200 ${isFiscalAutonomy ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label htmlFor="girardin" className="text-base font-medium">Éligible Girardin</Label>
                    <p className="text-sm text-stone-600 mt-1">Défiscalisation immobilière outre-mer</p>
                  </div>
                  <Switch
                    data-testid="switch-girardin"
                    id="girardin"
                    checked={!isFiscalAutonomy && formData.girardin_eligible}
                    onCheckedChange={(checked) => handleInputChange('girardin_eligible', checked)}
                    disabled={isFiscalAutonomy}
                  />
                </div>

                {formData.girardin_eligible && !isFiscalAutonomy && (
                  <div className="mt-4">
                    <Label htmlFor="girardin_reduction">Reduction fiscale annuelle (EUR)</Label>
                    <Input
                      data-testid="input-girardin-reduction"
                      id="girardin_reduction"
                      type="number"
                      placeholder="5000"
                      value={formData.girardin_reduction}
                      onChange={(e) => handleInputChange('girardin_reduction', e.target.value)}
                      className="h-12 rounded-xl"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-10 pt-6 border-t border-stone-200">
            {step > 1 && (
              <Button
                data-testid="btn-back"
                variant="outline"
                onClick={handleBack}
                className="rounded-full px-6"
              >
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Précédent
              </Button>
            )}
            
            {step < totalSteps ? (
              <Button
                data-testid="btn-next"
                onClick={handleNext}
                disabled={!isStepValid(step)}
                className="ml-auto rounded-full px-6 bg-[#005F73] hover:bg-[#005F73]/90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            ) : (
              <Button
                data-testid="btn-calculate"
                onClick={handleCalculate}
                disabled={!isStepValid(1) || !isStepValid(2) || !isStepValid(3)}
                className="ml-auto rounded-full px-8 bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Calculer la rentabilité
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
